import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCycle } from '../store/slices/cyclesSlice';
import { cyclesAPI } from '../services/api';
import { formatDateTime, downloadBlob } from '../utils/helpers';
import toast from 'react-hot-toast';

const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const UploadIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
  </svg>
);

const SyncIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 4 23 10 17 10"/>
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
  </svg>
);

const DownloadIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);

const SkipIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polygon points="5 4 15 12 5 20 5 4"/><line x1="19" y1="5" x2="19" y2="19"/>
  </svg>
);

export default function CycleDetailsPage() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { current: cycle, loading } = useSelector(s => s.cycles);
  const [stepLoading, setStepLoading] = useState({});
  const salaryRef = useRef();
  const pmsRef = useRef();

  useEffect(() => {
    dispatch(fetchCycle(id));
  }, [dispatch, id]);

  const setL = (key, val) => setStepLoading(prev => ({ ...prev, [key]: val }));

  const refreshCycle = () => dispatch(fetchCycle(id));

  // Step 1
  const handleSyncZoho = async () => {
    setL('sync', true);
    try {
      const { data } = await cyclesAPI.syncZoho(id);
      toast.success(`${data.records_synced} employee records synced.`);
      refreshCycle();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed.');
    } finally { setL('sync', false); }
  };

  // Step 2
  const handleSalaryUpload = async (file) => {
    if (!file) return;
    setL('salary', true);
    try {
      const { data } = await cyclesAPI.uploadSalaryHistory(id, file);
      toast.success(data.detail);
      refreshCycle();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed.');
    } finally { setL('salary', false); if (salaryRef.current) salaryRef.current.value = ''; }
  };

  // Step 3
  const handlePMSUpload = async (file) => {
    if (!file) return;
    setL('pms', true);
    try {
      const { data } = await cyclesAPI.uploadPMS(id, file);
      toast.success(data.detail);
      refreshCycle();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed.');
    } finally { setL('pms', false); if (pmsRef.current) pmsRef.current.value = ''; }
  };

  // Step 4
  const handleGenerate = async () => {
    setL('generate', true);
    try {
      const { data } = await cyclesAPI.generateRecommendations(id);
      toast.success(data.detail);
      refreshCycle();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed.');
    } finally { setL('generate', false); }
  };

  const handleDownload = async (type) => {
    setL(`dl_${type}`, true);
    try {
      const { data } = type === 'consolidated'
        ? await cyclesAPI.downloadConsolidated(id)
        : await cyclesAPI.downloadDeptwise(id);
      const ext = type === 'consolidated' ? 'xlsx' : 'zip';
      downloadBlob(data, `${cycle.cycle_id}_${type}.${ext}`);
    } catch { toast.error('Download failed.'); }
    finally { setL(`dl_${type}`, false); }
  };

  const handleSalaryTemplate = async () => {
    const { data } = await cyclesAPI.downloadSalaryTemplate();
    downloadBlob(data, 'salary_change_history_template.xlsx');
  };

  const handlePMSTemplate = async () => {
    const { data } = await cyclesAPI.downloadPMSTemplate();
    downloadBlob(data, 'pms_data_template.xlsx');
  };

  if (loading || !cycle) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><span className="spinner" style={{ width: 36, height: 36 }} /></div>;
  }

  const status = cycle.step_status || {};
  const lastSync = cycle.last_sync;

  const STEPS = [
    { key: 'step1', done: status.step1_done, label: 'Sync Zoho People Data' },
    { key: 'step2', done: status.step2_done, label: 'Upload Salary Change History' },
    { key: 'step3', done: status.step3_done, label: 'Upload Zoho PMS Data' },
    { key: 'step4', done: status.step4_done, label: 'Generate Salary Recommendations' },
  ];

  return (
    <div className="page-container">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            className="btn-icon magenta"
            onClick={() => navigate('/increment-cycles')}
            style={{ width: 34, height: 34, borderRadius: '50%', background: 'var(--magenta)', color: 'var(--white)' }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
            </svg>
          </button>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700 }}>Increment Cycle Details</h1>
            <div style={{ fontSize: 13, color: 'var(--grey-400)', marginTop: 2 }}>{cycle.cycle_id}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-outline btn-sm" onClick={handleSalaryTemplate}>
            <DownloadIcon /> Salary change history template
          </button>
          <button className="btn btn-outline btn-sm" onClick={handlePMSTemplate}>
            <DownloadIcon /> Zoho PMS data template
          </button>
          <button className="btn btn-navy btn-sm" onClick={() => navigate(`/increment-cycles/${id}/edit`)}>
            Edit Cycle Master data
          </button>
        </div>
      </div>

      {/* Steps card */}
      <div className="card" style={{ padding: 32 }}>
        {/* Stepper */}
        <div className="stepper" style={{ marginBottom: 40 }}>
          {STEPS.map((step, i) => (
            <div key={step.key} className={`step-item${step.done ? ' done' : i === STEPS.findIndex(s => !s.done) ? ' active' : ''}`}>
              <div className="step-circle">
                {step.done ? <CheckIcon /> : i + 1}
              </div>
              <div className="step-label">Step {i + 1}</div>
            </div>
          ))}
        </div>

        {/* Step cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>

          {/* Step 1 */}
          <StepCard
            done={status.step1_done}
            icon={<SyncIcon />}
            title="Sync Zoho People Data"
            subtitle={lastSync
              ? `Last Sync by ${lastSync.synced_by} on ${formatDateTime(lastSync.synced_at)}`
              : 'No sync yet'}
          >
            <button
              className="btn btn-primary btn-sm"
              style={{ justifyContent: 'center' }}
              disabled={stepLoading.sync}
              onClick={handleSyncZoho}
            >
              {stepLoading.sync
                ? <><span className="spinner" style={{ width: 13, height: 13 }} /> Syncing…</>
                : <><SyncIcon /> Sync Again</>}
            </button>
          </StepCard>

          {/* Step 2 */}
          <StepCard
            done={status.step2_done}
            icon={<UploadIcon />}
            title="Upload Salary Change History"
          >
            <input
              ref={salaryRef} type="file" accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={e => handleSalaryUpload(e.target.files[0])}
            />
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
              <button
                className="btn btn-outline btn-sm"
                disabled={stepLoading.salary}
                onClick={() => salaryRef.current.click()}
              >
                {stepLoading.salary
                  ? <><span className="spinner" style={{ width: 13, height: 13 }} /> Uploading…</>
                  : <><UploadIcon /> Upload File</>}
              </button>
              <button
                className="btn btn-outline btn-sm"
                style={{ color: 'var(--grey-500)', borderColor: 'var(--grey-300)' }}
                onClick={() => toast('Using previously uploaded data.')}
              >
                <SkipIcon /> Skip
              </button>
            </div>
          </StepCard>

          {/* Step 3 */}
          <StepCard
            done={status.step3_done}
            icon={<UploadIcon />}
            title="Upload Zoho PMS Data"
          >
            <input
              ref={pmsRef} type="file" accept=".xlsx,.xls"
              style={{ display: 'none' }}
              onChange={e => handlePMSUpload(e.target.files[0])}
            />
            <button
              className="btn btn-outline btn-sm"
              disabled={stepLoading.pms}
              onClick={() => pmsRef.current.click()}
            >
              {stepLoading.pms
                ? <><span className="spinner" style={{ width: 13, height: 13 }} /> Uploading…</>
                : <><UploadIcon /> Upload File</>}
            </button>
          </StepCard>

          {/* Step 4 */}
          <StepCard
            done={status.step4_done}
            icon={<DownloadIcon />}
            title="Generate Salary Recommendations"
          >
            {!status.step4_done ? (
              <button
                className="btn btn-primary btn-sm"
                style={{ justifyContent: 'center' }}
                disabled={stepLoading.generate || !status.step1_done}
                onClick={handleGenerate}
                title={!status.step1_done ? 'Complete Step 1 first' : ''}
              >
                {stepLoading.generate
                  ? <><span className="spinner" style={{ width: 13, height: 13 }} /> Generating…</>
                  : 'Generate'}
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
                <button
                  className="btn btn-outline btn-sm"
                  disabled={stepLoading.dl_consolidated}
                  onClick={() => handleDownload('consolidated')}
                >
                  {stepLoading.dl_consolidated
                    ? <span className="spinner" style={{ width: 13, height: 13 }} />
                    : <><DownloadIcon /> Download</>}
                </button>
                <button
                  className="btn btn-outline btn-sm"
                  disabled={stepLoading.dl_deptwise}
                  onClick={() => handleDownload('deptwise')}
                >
                  {stepLoading.dl_deptwise
                    ? <span className="spinner" style={{ width: 13, height: 13 }} />
                    : 'Dept-wise'}
                </button>
                <button
                  className="btn btn-primary btn-sm"
                  style={{ justifyContent: 'center' }}
                  onClick={() => navigate(`/recommendations/${id}`)}
                >
                  HOD Recommendations
                </button>
              </div>
            )}
          </StepCard>
        </div>
      </div>
    </div>
  );
}

function StepCard({ done, icon, title, subtitle, children }) {
  return (
    <div style={{
      border: '1px solid var(--grey-200)',
      borderRadius: 'var(--radius-lg)',
      padding: '20px 16px',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 12, textAlign: 'center',
      background: done ? '#F0FDF4' : 'var(--white)',
    }}>
      <div style={{
        color: done ? 'var(--success)' : 'var(--magenta)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 48, height: 48, borderRadius: '50%',
        background: done ? 'rgba(16,185,129,0.1)' : 'rgba(200,50,160,0.08)',
      }}>
        {done ? <CheckIcon /> : icon}
      </div>
      <div style={{ fontWeight: 700, fontSize: 13.5, color: done ? 'var(--grey-700)' : 'var(--grey-900)' }}>
        {title}
      </div>
      {subtitle && (
        <div style={{ fontSize: 11.5, color: 'var(--grey-400)', lineHeight: 1.5 }}>{subtitle}</div>
      )}
      {children}
    </div>
  );
}
